/**
 * Le census — l'effectif arrêté d'un dossier (chunk 9.BK.1).
 *
 * ## Ce que ce module tient
 *
 * Les contrats de fil et **la règle de complétude**, dans un collecteur pur
 * réutilisé par deux appelants : la route publique, qui fait autorité, et
 * l'écran, qui rend la main tout de suite. Motif `collectFieldRoleIssues` /
 * `collectBlockRefIssues` — une définition, deux appelants, aucune divergence
 * possible entre ce que l'écran annonce et ce que le serveur refuse.
 *
 * ## Deux invariants portés ici
 *
 * **`submitted` n'est pas `locked`.** Le premier dit que le client a déclaré
 * avoir terminé, le second que le conseiller a revu et accepté l'effectif pour
 * l'appel d'offres. Seul `locked` satisfait le requis d'envoi. Aucun helper de
 * ce module ne doit traiter l'un comme l'équivalent de l'autre.
 *
 * **Aucune normalisation silencieuse.** Les valeurs libres — `gender`, `smoker`,
 * `employment_status`, `coverage`, `employment_type`, `relationship` — sont
 * stockées **exactement telles que saisies**. Le classeur GFS ne contient aucune
 * liste déroulante : ce que le client tape est la donnée. Le collecteur *lit* ces
 * valeurs pour juger si un champ est renseigné, il ne les réécrit jamais. Le jour
 * où une valeur canonique s'ajoutera, ce sera à côté de la provenance, pas à sa
 * place.
 */

import { z } from "zod";

import { CensusCoverageChoiceSchema } from "./census-coverage.js";

// ---------------------------------------------------------------------------
// États
// ---------------------------------------------------------------------------

export const CENSUS_STATUSES = ["draft", "submitted", "locked"] as const;
export const CensusStatusSchema = z.enum(CENSUS_STATUSES);
export type CensusStatus = (typeof CENSUS_STATUSES)[number];

/**
 * Un census satisfait son requis **si et seulement si** il est verrouillé.
 *
 * Fonction plutôt que comparaison dispersée : c'est le seul endroit où cette
 * équivalence est écrite, et un test la sabote. `submitted` — « le client a
 * terminé » — ne suffit pas : personne du cabinet n'a encore regardé.
 */
export function censusSatisfiesRequirement(status: CensusStatus | null | undefined): boolean {
  return status === "locked";
}

// ---------------------------------------------------------------------------
// Plafonds
// ---------------------------------------------------------------------------

/**
 * Bornes de la surface publique. Une liste d'employés, pas un entrepôt.
 *
 * Elles vivent ici parce que l'écran doit les annoncer avant que le serveur ne
 * refuse : découvrir un plafond au 501ᵉ employé saisi serait cruel.
 */
export const CENSUS_LIMITS = {
  maxParticipants: 500,
  maxDependentsPerParticipant: 12,
} as const;

// ---------------------------------------------------------------------------
// Contrats de fil — colonnes métier UNIQUEMENT
// ---------------------------------------------------------------------------

const texte = (max: number) => z.string().max(max).nullable().optional();
const dateIso = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "date attendue au format AAAA-MM-JJ")
  .nullable()
  .optional();
const montant = (max: number) => z.number().min(0).max(max).nullable().optional();

/**
 * Une ligne d'employé telle que le client l'envoie.
 *
 * `.strict()` est la garde : un `census_id`, `project_id`, `tenant_id` ou
 * `company_id` glissé dans le corps fait **échouer** la requête. Il n'est pas
 * ignoré — un champ silencieusement écarté est une invitation à réessayer
 * autrement.
 *
 * Tout est optionnel : un brouillon incomplet doit pouvoir s'enregistrer. La
 * complétude se juge au passage à `submitted`, par contexte (cf. le collecteur).
 */
export const CensusParticipantInputSchema = z
  .object({
    employee_number: texte(60),
    first_name: texte(120),
    last_name: texte(120),
    birth_date: dateIso,
    gender: texte(40),
    mobile_phone: texte(60),
    email: texte(320),
    additional_email: texte(320),
    additional_phone: texte(60),
    hire_date: dateIso,
    employment_type: texte(60),
    division: texte(120),
    occupation: texte(120),
    annual_salary: montant(9_999_999),
    hourly_rate: montant(9_999),
    hours_per_week: montant(168),
    smoker: texte(40),
    coverage: texte(60),
    coverage_choice: CensusCoverageChoiceSchema.nullable().optional(),
    employment_status: texte(60),
    coverage_start: dateIso,
    coverage_end: dateIso,
    note: texte(200),
  })
  .strict();
export type CensusParticipantInput = z.infer<typeof CensusParticipantInputSchema>;

/**
 * Les champs du modèle, dérivés du schéma lui-même.
 *
 * Écrite à la main, cette liste finirait par diverger du contrat — et la
 * divergence se verrait le jour où `import_raw` accepterait une clé qui n'est
 * plus un champ.
 */
export const CHAMPS_PARTICIPANT_MODELE = Object.keys(CensusParticipantInputSchema.shape) as [
  keyof CensusParticipantInput,
  ...Array<keyof CensusParticipantInput>,
];

/**
 * Une personne à charge.
 *
 * Elle désigne son employé par **le `client_row_id` de celui-ci**, jamais par
 * notre identifiant interne : le client ne parle que dans ses propres
 * identifiants, et le serveur les résout dans le census du jeton. Un
 * `participant_id` accepté au fil serait un vecteur de substitution vers un
 * autre census.
 */
export const CensusDependentInputSchema = z
  .object({
    participant_client_row_id: z.string().uuid(),
    first_name: texte(120),
    last_name: texte(120),
    gender: texte(40),
    relationship: texte(60),
    birth_date: dateIso,
    smoker: texte(40),
    coverage_start: dateIso,
    coverage_end: dateIso,
    dependent_status: texte(60),
  })
  .strict();
export type CensusDependentInput = z.infer<typeof CensusDependentInputSchema>;

/** Créer le census d'un dossier. Route conseiller : un identifiant de dossier
 * dans le corps y est normal — l'interdiction ne vaut que pour la surface
 * publique, où le jeton est seul maître du périmètre. */
export const CreateCensusSchema = z.object({ project_id: z.string().uuid() }).strict();
export type CreateCensusInput = z.infer<typeof CreateCensusSchema>;

/** Retrouver le census d'un dossier. */
export const CensusQuerySchema = z.object({ project_id: z.string().uuid() }).strict();

/**
 * Créer le lien magique qui mène le client à SA liste.
 *
 * `contact_id` est le destinataire prévu : il alimente la provenance, comme sur
 * un lien de formulaire (9.BJ.3/9.BJ.7). Optionnel — sans lui, le lien reste
 * anonyme plutôt que de désigner quelqu'un au hasard.
 */
export const CreateCensusLinkSchema = z
  .object({
    expires_in_days: z.union([z.literal(7), z.literal(30), z.literal(90), z.null()]).optional(),
    contact_id: z.string().uuid().nullable().optional(),
  })
  .strict();
export type CreateCensusLinkInput = z.infer<typeof CreateCensusLinkSchema>;

// ---------------------------------------------------------------------------
// Import du classeur — chunk 9.BK.2
// ---------------------------------------------------------------------------

/** Demander l'analyse d'un classeur déjà déposé. **N'écrit rien.** */
export const PreviewCensusImportSchema = z.object({ file_id: z.string().uuid() }).strict();
export type PreviewCensusImportInput = z.infer<typeof PreviewCensusImportSchema>;

/**
 * Une ligne telle qu'elle sort de la revue d'import.
 *
 * `import_raw` est **borné aux champs du modèle** et à des valeurs de cellule :
 * sans ces bornes, ce serait un sac à contenu arbitraire sur une colonne
 * `jsonb`. Les clés sont validées contre la liste des champs, la taille est
 * plafonnée, et le tout reste optionnel.
 */
export const ImportedCensusParticipantSchema = CensusParticipantInputSchema.extend({
  import_raw: z
    .record(z.enum(CHAMPS_PARTICIPANT_MODELE), z.string().min(1).max(300))
    .refine((v) => Object.keys(v).length > 0, "import_raw vide : omettez-le plutôt")
    .optional(),
}).strict();
export type ImportedCensusParticipantInput = z.infer<typeof ImportedCensusParticipantSchema>;

/**
 * Appliquer le remplacement.
 *
 * Les lignes viennent de **l'écran de revue**, pas d'une seconde analyse du
 * fichier : ce que le conseiller confirme est ce qui s'écrit. Réanalyser à
 * l'application écrirait quelque chose qu'il n'a pas vu — et rendrait ses
 * corrections invisibles.
 *
 * `file_id` voyage quand même : c'est la **provenance**, et la trace d'import
 * pointe le classeur qui fait foi.
 */
export const ApplyCensusImportSchema = z
  .object({
    coverage_review_revision: z
      .string()
      .regex(/^[a-f0-9]{64}$/)
      .optional(),
    file_id: z.string().uuid(),
    /** Ce que l'analyse avait compris, tel que la revue l'a laissé. */
    sheet_name: z.string().min(1).max(200),
    header_row: z.number().int().min(1),
    rows_detected: z.number().int().min(0),
    anomalies_count: z.number().int().min(0).max(100_000),
    anomaly_kinds: z.array(z.string().max(60)).max(20),
    unknown_columns: z.array(z.string().max(300)).max(64),
    /**
     * Les lignes confirmées — mêmes colonnes qu'une saisie manuelle, **plus**
     * `import_raw` pour ce que l'analyse n'a pas su lire.
     *
     * `import_raw` n'existe que sur ce contrat : une saisie manuelle n'a rien à
     * y mettre, et l'y autoriser ouvrirait un champ libre sur une route où rien
     * ne le justifie.
     */
    rows: z.array(ImportedCensusParticipantSchema).min(1).max(CENSUS_LIMITS.maxParticipants),
  })
  .strict();
export type ApplyCensusImportInput = z.infer<typeof ApplyCensusImportSchema>;

/**
 * L'annualisation d'une rémunération horaire — **une suggestion, jamais une
 * écriture**.
 *
 * Rend `null` dès qu'il manque une des deux valeurs : on ne peut pas annualiser
 * un taux sans savoir combien d'heures il couvre, et inventer 40 h pour combler
 * le trou serait précisément l'écriture silencieuse que le PO interdit.
 *
 * 52 semaines, sans retenue pour vacances : c'est la convention du classeur
 * source, et la valeur reste **proposée** — le conseiller la corrige s'il le
 * faut.
 */
export function suggererSalaireAnnuel(
  tauxHoraire: number | null | undefined,
  heuresParSemaine: number | null | undefined,
): number | null {
  if (typeof tauxHoraire !== "number" || !Number.isFinite(tauxHoraire)) return null;
  if (typeof heuresParSemaine !== "number" || !Number.isFinite(heuresParSemaine)) return null;
  if (tauxHoraire <= 0 || heuresParSemaine <= 0) return null;
  return Math.round(tauxHoraire * heuresParSemaine * 52 * 100) / 100;
}

/** Le motif du déverrouillage — obligatoire, et non vide. */
export const UnlockCensusSchema = z.object({ reason: z.string().trim().min(1).max(500) }).strict();
export type UnlockCensusInput = z.infer<typeof UnlockCensusSchema>;

/**
 * Remettre un effectif en saisie — `submitted → draft`.
 *
 * Même forme que le déverrouillage, et pour la même raison : un geste qui
 * annule la déclaration du client doit dire **pourquoi**, sans quoi la trace ne
 * sert à personne six mois plus tard.
 *
 * Deux schémas plutôt qu'un alias : ce sont deux gestes distincts, et le jour
 * où l'un gagne un champ, l'alias les ferait diverger en silence.
 */
export const ReopenCensusSchema = z.object({ reason: z.string().trim().min(1).max(500) }).strict();
export type ReopenCensusInput = z.infer<typeof ReopenCensusSchema>;

// ---------------------------------------------------------------------------
// La complétude — contextuelle, jamais « aucun NULL »
// ---------------------------------------------------------------------------

/**
 * Pourquoi une ligne n'est pas prête. Des motifs **énumérés**, pas des phrases :
 * le front les localise, il ne les interprète jamais par expression régulière.
 */
export const CENSUS_ISSUE_REASONS = [
  "required",
  "remuneration_missing",
  "invalid_date",
  "hire_before_birth",
  "coverage_end_before_start",
  "duplicate_employee_number",
  "orphan_dependent",
  /**
   * 9.BK.2 — le classeur portait quelque chose, et on n'a pas su le lire.
   *
   * Distinct de `required` (le client n'a rien écrit) et de `invalid_date` (on
   * sait que c'était censé être une date) : ici on sait seulement que la valeur
   * existe et qu'elle est conservée telle quelle, à côté du champ.
   */
  "unreadable_value",
] as const;
export type CensusIssueReason = (typeof CENSUS_ISSUE_REASONS)[number];

export type CensusRowIssue = {
  /** `participant` ou `dependent` — la ligne visée. */
  row: "participant" | "dependent";
  /** L'identifiant que le client connaît : le sien. */
  client_row_id: string;
  /** La colonne en cause, ou `null` quand le défaut porte sur la ligne entière. */
  field: string | null;
  reason: CensusIssueReason;
};

/** Une valeur est-elle renseignée ? Lecture seule — rien n'est réécrit. */
function rempli(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === "number") return Number.isFinite(v);
  return String(v).trim().length > 0;
}

/** Une date ISO valide au calendrier, pas seulement à la forme. */
function dateValide(v: unknown): boolean {
  if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}

/**
 * `import_raw` porte les champs que l'analyse du classeur n'a pas su lire.
 *
 * Il n'est pas facultatif par confort : une ligne saisie à la main n'en a
 * jamais, et une ligne importée n'en a que si quelque chose a résisté.
 */
type LigneParticipant = CensusParticipantInput & {
  client_row_id: string;
  import_raw?: Record<string, string> | null;
};
type LigneDependant = CensusDependentInput & { client_row_id: string };

/**
 * Les champs sans lesquels un employé ne peut pas être tarifé.
 *
 * **Volontairement court.** Le classeur écrit « toutes les colonnes doivent être
 * complétées », mais il porte aussi des colonnes qui ne s'appliquent pas à tout
 * le monde — un employé sans cellulaire existe, un employeur sans divisions
 * aussi. Une garde « toute valeur nulle est une erreur » rendrait le census
 * impossible à terminer, et c'est précisément ce qu'il ne faut pas écrire.
 */
const CHAMPS_REQUIS_PARTICIPANT = [
  "employee_number",
  "first_name",
  "last_name",
  "birth_date",
  "hire_date",
  "coverage",
] as const;

/** Les champs d'un participant qui sont des dates — pour nommer le bon manque. */
const CHAMPS_DATE_PARTICIPANT = new Set([
  "birth_date",
  "hire_date",
  "coverage_start",
  "coverage_end",
]);

const CHAMPS_REQUIS_DEPENDANT = ["first_name", "last_name", "relationship", "birth_date"] as const;

/**
 * Collecte ce qui empêche un census d'être déclaré terminé.
 *
 * Rend une **liste** de `{ ligne, champ, motif }`, jamais un booléen : dire à
 * quelqu'un « votre liste est incomplète » sans lui dire où ne lui permet
 * d'agir sur rien.
 *
 * Une liste vide signifie « prêt à passer en `submitted` » — et rien d'autre :
 * le verrou, lui, reste un geste du conseiller.
 */
export function collectCensusRowIssues(entree: {
  participants: ReadonlyArray<LigneParticipant>;
  dependents: ReadonlyArray<LigneDependant>;
}): CensusRowIssue[] {
  const issues: CensusRowIssue[] = [];
  const vus = new Map<string, string>();

  for (const p of entree.participants) {
    const ajoute = (field: string | null, reason: CensusIssueReason) =>
      issues.push({ row: "participant", client_row_id: p.client_row_id, field, reason });

    // Ce que le classeur disait et qu'on n'a pas su lire.
    //
    // Une entrée ici vaut un manque : la colonne typée est vide **parce que**
    // la valeur était illisible, pas parce que le client n'a rien écrit. Dire
    // « champ requis » serait faux et enverrait le conseiller chercher une
    // information qu'il a déjà sous les yeux.
    //
    // C'est ce manque qui tient l'invariant : tant qu'il est là, `submitted` et
    // `locked` refusent.
    const brut = p.import_raw ?? {};
    if (
      p.coverage_choice != null &&
      !CensusCoverageChoiceSchema.safeParse(p.coverage_choice).success
    )
      ajoute("coverage_choice", "unreadable_value");

    for (const champ of CHAMPS_REQUIS_PARTICIPANT) {
      if (champ === "coverage" && CensusCoverageChoiceSchema.safeParse(p.coverage_choice).success)
        continue;
      if (champ in brut) continue; // traité juste en dessous, et plus précisément
      if (!rempli(p[champ])) ajoute(champ, "required");
    }

    for (const champ of Object.keys(brut)) {
      ajoute(champ, CHAMPS_DATE_PARTICIPANT.has(champ) ? "invalid_date" : "unreadable_value");
    }

    // La rémunération accepte DEUX formes, parce que le vrai classeur en fournit
    // deux. Exiger le salaire annuel refuserait une liste remplie à l'horaire.
    const annuel = rempli(p.annual_salary);
    const horaire = rempli(p.hourly_rate) && rempli(p.hours_per_week);
    if (!annuel && !horaire) ajoute("annual_salary", "remuneration_missing");

    for (const champ of ["birth_date", "hire_date", "coverage_start", "coverage_end"] as const) {
      if (rempli(p[champ]) && !dateValide(p[champ])) ajoute(champ, "invalid_date");
    }
    if (dateValide(p.birth_date) && dateValide(p.hire_date) && p.hire_date! < p.birth_date!) {
      ajoute("hire_date", "hire_before_birth");
    }
    if (
      dateValide(p.coverage_start) &&
      dateValide(p.coverage_end) &&
      p.coverage_end! < p.coverage_start!
    ) {
      ajoute("coverage_end", "coverage_end_before_start");
    }

    // Le numéro rattache les personnes à charge à l'import : ambigu, il serait
    // faux. La base le refuse déjà ; on le dit ici pour le dire AVANT.
    if (rempli(p.employee_number)) {
      const cle = String(p.employee_number).trim();
      const deja = vus.get(cle);
      if (deja !== undefined) ajoute("employee_number", "duplicate_employee_number");
      else vus.set(cle, p.client_row_id);
    }
  }

  const connus = new Set(entree.participants.map((p) => p.client_row_id));
  for (const d of entree.dependents) {
    const ajoute = (field: string | null, reason: CensusIssueReason) =>
      issues.push({ row: "dependent", client_row_id: d.client_row_id, field, reason });

    if (!connus.has(d.participant_client_row_id)) ajoute(null, "orphan_dependent");

    for (const champ of CHAMPS_REQUIS_DEPENDANT) {
      if (!rempli(d[champ])) ajoute(champ, "required");
    }
    for (const champ of ["birth_date", "coverage_start", "coverage_end"] as const) {
      if (rempli(d[champ]) && !dateValide(d[champ])) ajoute(champ, "invalid_date");
    }
    if (
      dateValide(d.coverage_start) &&
      dateValide(d.coverage_end) &&
      d.coverage_end! < d.coverage_start!
    ) {
      ajoute("coverage_end", "coverage_end_before_start");
    }
  }

  return issues;
}

/** Receipt metadata only; no signed URL, Storage path or employee value. */
export const CensusWorkbookReceiptSchema = z.object({
  id: z.string().uuid(),
  file_name: z.string(),
  size_bytes: z.union([z.number().nonnegative(), z.string().regex(/^\d+$/)]),
  uploaded_at: z.string(),
  imported: z.boolean(),
});
export type CensusWorkbookReceipt = z.infer<typeof CensusWorkbookReceiptSchema>;
